import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, ElementRef, OnDestroy } from '@angular/core';
import { ConfigColumn, TypeColumn, NumberColumnPipe, CellAlignmentEnum } from '@eqproject/eqp-table';
import { DxDataGridComponent } from 'devextreme-angular';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'fp-dx-datagrid',
  templateUrl: './fp-dx-datagrid.component.html',
  styleUrls: ['./fp-dx-datagrid.component.scss']
})
export class FpDxDataGridComponent implements OnInit, OnChanges, OnDestroy {
  TypeColumn = TypeColumn;
  CellAlignmentEnum = CellAlignmentEnum;

  // --- INPUT FOR OVERRIDING PROPERTIES ---
  @Input() pageSize: number = 10;
  @Input() allowedPageSizes: any[] = [5, 10, 20, 50, 100];
  @Input() gridHeight: string | number;
  @Input() showPaginator: boolean = true;
  @Input() keyExpr: string = "ID";
  @Input() gridId: string; // Identifier for saving settings like pageSize
  @Input() summary: any;

  // Properties for compatibility with legacy dx-table-wrapper
  @Input() switchFilterType: boolean = true;
  @Input() isSingleColumnFilter: boolean = true;
  @Input() singleFilterColumnVisible: boolean = true;
  @Input() showSingleFilterButton: boolean = true;
  @Input() switchFilterToSingleText: string;
  @Input() switchFilterToGenericText: string;
  @Input() currencyFilterToReplace: string;
  @Input() currencyFilterToUse: string;
  // ---------------------------------------

  @Input() columns: ConfigColumn[];
  @Input() data: any[];
  @Input() isMultiLanguage: boolean;
  @Input() exportEqpTable: any;
  @Input() emptyTableMessage: string;
  @Input() createMatCardHeader: boolean; 
  @Input() createMatCard: boolean;
  @Input() headerTitle: string;

  @Input() selection: boolean;
  @Input() isMultipleSelection: boolean;

  @Input() groupPanel: any = { visible: true };
  @Input() export: any = { enabled: true };
  @Input() columnChooser: any = { enabled: true };
  @Input() searchPanel: any = { visible: true };
  @Input() filterRow: any;

  @Output() checkboxInfo = new EventEmitter<any>();
  @Output() eqpExportStarted = new EventEmitter<any>();
  @Output() eqpExportCompleted = new EventEmitter<any>();

  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  showFilterRow: boolean = true;
  menuVisible: boolean = false;
  menuTarget: any;
  currentActions: any[] = [];
  currentRowData: any;
  processedColumns: any[] = [];
  
  exportMenuVisible: boolean = false;
  exportMenuTarget: any;
  exportMenuItems: any[] = [];

  internalSummary: any = { totalItems: [], groupItems: [] };
  private langChangeSubscription: Subscription;
  private saveTimeout: any;

  constructor(private translate: TranslateService, private elementRef: ElementRef) { }

  ngOnInit() {
    this.loadPageSize();
    if (this.filterRow && this.filterRow.visible !== undefined) {
      this.showFilterRow = this.filterRow.visible;
    } else {
      this.showFilterRow = this.isSingleColumnFilter;
    }
    this.updateProcessedColumns();
    this.initializeSummary();
    
    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.updateSummaryTranslations();
    });
  }

  ngOnDestroy() {
      if (this.langChangeSubscription) {
          this.langChangeSubscription.unsubscribe();
      }
  }

  private updateSummaryTranslations() {
      if (!this.internalSummary) return;

      const updateItems = (items: any[]) => {
          if (!items) return;
          items.forEach(item => {
              if (item.summaryType === 'count') {
                  item.displayFormat = this.translate.instant('DATAGRID.COUNT_DISPLAY_FORMAT');
              }
          });
      };

      updateItems(this.internalSummary.totalItems);
      updateItems(this.internalSummary.groupItems);
  }

  private getStorageKey(type: string = 'pageSize'): string {
      if (this.gridId) {
          return `fp-dx-datagrid-${type}-${this.gridId}`;
      }
      if (this.elementRef) {
          const id = this.elementRef.nativeElement.getAttribute('id');
          if (id) {
              return `fp-dx-datagrid-${type}-${id}`;
          }
      }
      return null;
  }

  onOptionChanged(e: any) {
      if (e.fullName === 'paging.pageSize') {
          const storageKey = this.getStorageKey();
          if (storageKey) {
              localStorage.setItem(storageKey, JSON.stringify(e.value));
          }
      }
      
      if (e.fullName.startsWith('columns')) {
          if (this.saveTimeout) clearTimeout(this.saveTimeout);
          this.saveTimeout = setTimeout(() => {
              this.saveColumnState(e.component);
          }, 1000); 
      }
  }

  private saveColumnState(component: any) {
      const storageKey = this.getStorageKey('columns');
      if (!storageKey) return;
      
      const columnsState = [];
      
      if (this.processedColumns) {
          this.processedColumns.forEach(col => {
              if (col.key) {
                  try {
                      columnsState.push({
                          dataField: col.key,
                          name: component.columnOption(col.key, "name") || col.name,
                          width: component.columnOption(col.key, "width"),
                          visibleIndex: component.columnOption(col.key, "visibleIndex"),
                          visible: component.columnOption(col.key, "visible")
                      });
                  } catch (e) { }
              }
          });
      }
      
      localStorage.setItem(storageKey, JSON.stringify(columnsState));
  }

  private loadAndApplyColumnState() {
      const storageKey = this.getStorageKey('columns');
      if (!storageKey || !this.processedColumns) return;

      const savedStateJson = localStorage.getItem(storageKey);
      if (!savedStateJson) return;

      const savedState = JSON.parse(savedStateJson);
      if (!Array.isArray(savedState)) return;

      this.processedColumns.forEach(col => {
          const savedCol = savedState.find(s => s.dataField === col.key);
          if (savedCol) {
              if (savedCol.width) {
                  if (!col.styles) col.styles = {};
                  col.styles.width = savedCol.width;
              }
              if (savedCol.visible !== undefined) {
                  col.hidden = !savedCol.visible;
              }
              col['_visibleIndex'] = savedCol.visibleIndex;
          }
      });

      this.processedColumns.sort((a, b) => {
           const ia = a['_visibleIndex'];
           const ib = b['_visibleIndex'];
           if (ia !== undefined && ib !== undefined) return ia - ib;
           if (ia !== undefined) return -1;
           if (ib !== undefined) return 1;
           return 0;
      });
  }

  loadPageSize() {
      const storageKey = this.getStorageKey();
      if (!storageKey) return;
      const savedPageSize = localStorage.getItem(storageKey);
      if (savedPageSize) {
          const parsed = parseInt(JSON.parse(savedPageSize));
          if (!isNaN(parsed)) {
              this.pageSize = parsed;
          }
      }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.columns || changes.gridId) {
      this.updateProcessedColumns();
    }
     if (changes.summary) {
        this.initializeSummary();
     }
  }

  initializeSummary() {
      if (this.summary) {
          this.internalSummary = {
              totalItems: [...(this.summary.totalItems || [])],
              groupItems: [...(this.summary.groupItems || [])]
          };
      } else {
          this.internalSummary = { totalItems: [], groupItems: [] };
      }
  }

  addSummary(dataField: string, type: string) {
       const col = this.columns.find(c => c.key === dataField);
       const format = this.getFormat(col);
       
       const newItem = {
          column: dataField,
          summaryType: type,
          showInGroupFooter: true,
          alignByColumn: true,
          displayFormat: type === 'count' ? this.translate.instant('DATAGRID.COUNT_DISPLAY_FORMAT') : '{0}',
          valueFormat: format,
          name: `${dataField}_${type}`
      };

      const existingIndex = this.internalSummary.groupItems.findIndex(i => i.column === dataField && i.summaryType === type);
      if (existingIndex === -1) {
          this.internalSummary.groupItems.push(newItem);
      } else {
          // If exist update it? Or ignore?
      }
      // Force repaint or rebind? internalSummary should trigger change detection if bound in template
  }

  removeSummary(dataField: string) {
       this.internalSummary.groupItems = this.internalSummary.groupItems.filter(i => i.column !== dataField);
  }

  addTotalSummary(dataField: string, type: string) {
       const col = this.columns.find(c => c.key === dataField);
       const format = this.getFormat(col);

       const newItem = {
          column: dataField,
          summaryType: type,
          displayFormat: type === 'count' ? this.translate.instant('DATAGRID.COUNT_DISPLAY_FORMAT') : '{0}',
          valueFormat: format,
          name: `${dataField}_${type}_total`
      };

      const existingIndex = this.internalSummary.totalItems.findIndex(i => i.column === dataField && i.summaryType === type);
      if (existingIndex === -1) {
           this.internalSummary.totalItems.push(newItem);
      }
  }

  removeTotalSummary(dataField: string) {
      this.internalSummary.totalItems = this.internalSummary.totalItems.filter(i => i.column !== dataField);
  }

  onContextMenuPreparing(e: any) {
      if (e.target === 'header') {
          if (!e.column.dataField) return; // Ignore band columns or special columns
          
          if (!e.items) e.items = [];
          
          e.items.push(
              {
                  text: this.translate.instant('DATAGRID.HIDE_COLUMN'),
                  icon: "columnchooser",
                  onItemClick: () => {
                      e.component.columnOption(e.column.dataField, "visible", false);
                  }
              }
          );

          const column = this.getColumnConfig(e.column);
          if (column && this.isNumber(column)) {
              if (!e.items) e.items = [];
              e.items.push(
                  {
                      text: this.translate.instant('DATAGRID.GROUP_SUMMARY'),
                      items: [
                          { text: this.translate.instant('DATAGRID.SUM'), onItemClick: () => this.addSummary(e.column.dataField, 'sum') },
                          { text: this.translate.instant('DATAGRID.MIN'), onItemClick: () => this.addSummary(e.column.dataField, 'min') },
                          { text: this.translate.instant('DATAGRID.MAX'), onItemClick: () => this.addSummary(e.column.dataField, 'max') },
                          { text: this.translate.instant('DATAGRID.AVG'), onItemClick: () => this.addSummary(e.column.dataField, 'avg') },
                          { text: this.translate.instant('DATAGRID.COUNT'), onItemClick: () => this.addSummary(e.column.dataField, 'count') },
                          { text: this.translate.instant('DATAGRID.REMOVE_ALL'), onItemClick: () => this.removeSummary(e.column.dataField) }
                      ]
                  },
                  {
                      text: this.translate.instant('DATAGRID.TOTAL_SUMMARY'),
                      items: [
                          { text: this.translate.instant('DATAGRID.SUM'), onItemClick: () => this.addTotalSummary(e.column.dataField, 'sum') },
                          { text: this.translate.instant('DATAGRID.MIN'), onItemClick: () => this.addTotalSummary(e.column.dataField, 'min') },
                          { text: this.translate.instant('DATAGRID.MAX'), onItemClick: () => this.addTotalSummary(e.column.dataField, 'max') },
                          { text: this.translate.instant('DATAGRID.AVG'), onItemClick: () => this.addTotalSummary(e.column.dataField, 'avg') },
                          { text: this.translate.instant('DATAGRID.COUNT'), onItemClick: () => this.addTotalSummary(e.column.dataField, 'count') },
                          { text: this.translate.instant('DATAGRID.REMOVE_ALL'), onItemClick: () => this.removeTotalSummary(e.column.dataField) }
                      ]
                  }
              );
          }
      }
  }

  getTemplateName(col: ConfigColumn): string {
    if (this.isActionColumn(col)) {
      if (col.actions && col.actions.length === 1) return 'simpleActionTemplate';
      return 'actionTemplate';
    } 
    if (this.isExternalTemplate(col) && col.externalTemplate) return 'externalTemplateName';
    if (col.type === TypeColumn.Enum) return 'enumTemplate';
    if (col.type === TypeColumn.Boolean) return 'booleanTemplate';
    if (col.type === TypeColumn.Date) return 'dateTemplate';
    return undefined;
  }

  isExternalTemplate(col: ConfigColumn): boolean {
      return (col.type as any) == TypeColumn.ExternalTemplate || (col.type as any) == 6;
  }

  updateProcessedColumns() {
     if (!this.columns) {
         this.processedColumns = [];
         return;
     }
     this.processedColumns = this.columns.map(col => {
         return { ...col, styles: col.styles ? { ...col.styles } : {}, calculateCellValue: this.createCalculateCellValue(col) };
     });
     this.loadAndApplyColumnState();
  }

  toggleFilter() { this.showFilterRow = !this.showFilterRow; }

  openActionMenu(event: any, rowData: any, actions: any[]) {
    event.stopPropagation();
    if (!actions || actions.length === 0) return;
    this.menuTarget = event.target;
    this.currentRowData = rowData;
    this.currentActions = actions.filter(a => {
      if (a.hidden && typeof a.hidden === 'function') return !a.hidden(rowData);
      return true;
    });
    if (this.currentActions.length > 0) this.menuVisible = true;
  }

  executeAction(action: any, rowData: any = null) {
    this.menuVisible = false;
    const data = rowData || this.currentRowData;
    if (action.fn) action.fn(data);
  }

  isSimpleAction(col: ConfigColumn): boolean {
    return (col.type as any) === TypeColumn.SimpleAction || (col.type as any) === 4; 
  }

  isActionColumn(col: any): boolean {
    return col.type === TypeColumn.MenuAction || col.key === 'action' || col.key === 'options' || this.isSimpleAction(col);
  }

  getActions(dxColumn: any): any[] {
     if(!dxColumn) return [];
     const colConfig = this.columns.find(c => c.key === dxColumn.dataField);
     return colConfig ? colConfig.actions : [];
  }

  getColumnConfig(dxColumn: any): ConfigColumn | undefined {
    if (!dxColumn) return undefined;
    return this.columns.find(c => c.key === dxColumn.dataField);
  }

  onSelectionChanged(e: any) { this.checkboxInfo.emit(e.selectedRowsData); }

  resetLayout() {
      const storageKey = this.getStorageKey('columns');
      const pageSizeKey = this.getStorageKey('pageSize');
      
      if (storageKey) localStorage.removeItem(storageKey);
      if (pageSizeKey) localStorage.removeItem(pageSizeKey);
      
      this.dataGrid.instance.state(null);
      
      // Force reload to apply default columns from inputs
      this.updateProcessedColumns();
  }

  onToolbarPreparing(e: any) {
    if (e.toolbarOptions && e.toolbarOptions.items) {
        e.toolbarOptions.items.unshift({
            location: 'after',
            widget: 'dxButton',
            options: {
                icon: 'refresh',
                hint: 'Reimposta layout',
                onClick: () => {
                    this.resetLayout();
                }
            }
        });
    }

    if (this.isMultipleExportMode()) {
       const toolbarItems = e.toolbarOptions.items;
       const exportItem = toolbarItems.find(item => item.name === 'exportButton');
       if (exportItem) {
           const index = toolbarItems.indexOf(exportItem);
           toolbarItems.splice(index, 1);
           toolbarItems.push({ location: 'after', template: 'exportMenuTemplate' });
       }
    }
  }

  isMultipleExportMode(): boolean {
    if (!this.exportEqpTable) return false;
    if (Array.isArray(this.exportEqpTable.exportFileType)) return true;
    if (this.exportEqpTable.exportFileType !== undefined && this.exportEqpTable.exportFileType !== null) return false;
    return true;
  }

  displayExportMenu(e: any) {
      this.exportMenuTarget = e.element;
      this.exportMenuItems = this.getExportMenuItems();
      this.exportMenuVisible = true;
  }

  getExportMenuItems(): any[] {
      if (this.exportEqpTable && Array.isArray(this.exportEqpTable.exportFileType)) {
          return this.exportEqpTable.exportFileType.map(t => ({ text: t, value: t }));
      }
      return [{ text: 'xlsx', value: 'xlsx' }, { text: 'csv', value: 'csv' }, { text: 'txt', value: 'txt' }];
  }

  triggerCustomExport(format: string = null) {
        this.eqpExportStarted.emit();
        if (this.exportEqpTable && this.exportEqpTable.customExportFunction) {
            const customFuncs = Array.isArray(this.exportEqpTable.customExportFunction) 
                                ? this.exportEqpTable.customExportFunction 
                                : [this.exportEqpTable.customExportFunction];
    
            let indexToRun = -1;

            if (format) {
                 if (Array.isArray(this.exportEqpTable.exportFileType)) {
                     indexToRun = this.exportEqpTable.exportFileType.indexOf(format);
                 } else {
                     if (format === 'xlsx') indexToRun = 0;
                     else if (format === 'csv') indexToRun = 1;
                     else if (format === 'txt') indexToRun = 2;
                 }
            } else {
                indexToRun = 0;
            }

            if (customFuncs.length > 0) {
                 if (indexToRun >= customFuncs.length) {
                     indexToRun = customFuncs.length - 1; 
                 }
                 if (indexToRun < 0) indexToRun = 0;

                 const fn = customFuncs[indexToRun];
                 if (typeof fn === 'function') {
                     fn(format); 
                 }
            }
        }
        setTimeout(() => this.eqpExportCompleted.emit(), 1000); 
  }

  onExporting(e: any) {
    if (this.exportEqpTable && this.exportEqpTable.customExportFunction) {
        e.cancel = true;
        this.triggerCustomExport();
    } else {
        this.eqpExportStarted.emit();
        setTimeout(() => this.eqpExportCompleted.emit(), 1000); 
    }
  }

  resolveNestedProperty(obj: any, path: string): any {
    if (!obj || !path) return null;
    return path.split('.').reduce((prev, curr) => prev ? prev[curr] : null, obj);
  }

  getDisplayValue(value: any, col: ConfigColumn, row: any): string {
    if (col.value) {
       if (typeof col.value === 'function') return (col.value as Function)(row);
       else if (typeof col.value === 'string') {
          const resolved = this.resolveNestedProperty(row, col.value);
          if (resolved !== null && resolved !== undefined) return resolved; 
       }
    }
    return value;
  }

  getEnumDisplay(value: any, col: ConfigColumn): string {
    if (value === null || value === undefined) return "";
    let keyToTranslate = value;
    if (col.enumModel) {
       const foundKey = Object.keys(col.enumModel).find(k => col.enumModel[k] === value);
       if (foundKey && isNaN(Number(foundKey))) keyToTranslate = foundKey;
    }
    if (col.multilanguagePrefixKey) return this.translate.instant(col.multilanguagePrefixKey + keyToTranslate);
    return keyToTranslate;
  }

  createCalculateCellValue(col: ConfigColumn) {
    return (rowData: any) => {
        let val;
        // Se la key contiene punti, risolvo la proprietà annidata
        // Altrimenti prendo il valore diretto (se esiste) o provo comunque a risolvere se la row potrebbe avere la key come proprietà diretta
        if (col.key && col.key.includes('.')) {
            val = this.resolveNestedProperty(rowData, col.key);
        } else {
            val = rowData[col.key];
        }

        if (col.value) {
           if (typeof col.value === 'function') val = col.value(rowData);
           else if (typeof col.value === 'string') val = this.resolveNestedProperty(rowData, col.value);
        }
        if (this.isNumber(col) && val !== null && val !== undefined && val !== '') {
             if (typeof val === 'string') { 
                 const cleanVal = val.replace(/[^0-9,.-]/g, '');
                 if (cleanVal.includes(',')) {
                    const normalized = cleanVal.replace(/\./g, '').replace(',', '.');
                    if (!isNaN(Number(normalized))) return Number(normalized);
                 }
                 if (!isNaN(Number(cleanVal))) return Number(cleanVal);
             }
             return Number(val);
        }
        return val;
    };
  }

  getBooleanHtml(value: any, col: ConfigColumn): string | null {
    if (col.booleanValues && value !== null && value !== undefined) {
      return col.booleanValues[String(value)];
    }
    return null;
  }

  getFormat(col: ConfigColumn): any {
    if ((col.numberPipe as any) == 2 || col.currencyPipeCode === 'EUR') {
        return { type: 'currency', precision: 2, currency: typeof col.currencyPipeCode === 'string' ? col.currencyPipeCode : 'EUR' };
    }
    if (col.type === TypeColumn.Date) return 'dd/MM/yyyy';
    return col.format;
  }

  getDataType(col: ConfigColumn): string {
    if (col.type === TypeColumn.Date) return 'date';
    if (col.type === TypeColumn.Boolean) return 'boolean';
    if (this.isNumber(col)) return 'number';
    return 'string';
  }

  isNumber(col: ConfigColumn): boolean {
    return (col.numberPipe !== undefined && col.numberPipe !== null) || col.currencyPipeCode === 'EUR';
  }

  getWidth(col: ConfigColumn): string | undefined {
    const styles: any = col.styles;

    if (styles && styles.width) {
        return styles.width;
    }
    
    if (styles && styles.flex) {
        // Gestione avanzata flex: "0 0 4%" oppure "4%"
        const parts = styles.flex.split(' ');
        // Se troviamo 3 parti (es. "0 0 4%"), prendiamo l'ultima che è width/basis
        if (parts.length === 3) {
            return parts[2]; 
        }
        // Altrimenti proviamo a usare direttamente il valore
        return styles.flex;
    }
    
    return undefined;
  }

  getAlignment(col: ConfigColumn): string {
    if (col.styles?.cellAlignment) {
      if (col.styles.cellAlignment === CellAlignmentEnum.RIGHT) return 'right';
      if (col.styles.cellAlignment === CellAlignmentEnum.CENTER) return 'center';
      if (col.styles.cellAlignment === CellAlignmentEnum.LEFT) return 'left';
    }
    if (this.isNumber(col)) {
        return 'right'; 
    }
    return 'left';
  }

  getIcon(action: any, rowData: any): string {
    return typeof action.icon === 'function' ? action.icon(rowData) : action.icon;
  }
  
  isActionDisabled(action: any, rowData: any = null): boolean {
    const data = rowData || this.currentRowData;
    return (action.disabled && typeof action.disabled === 'function') ? action.disabled(data) : !!action.disabled;
  }

  reloadDatatable() {
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.refresh();
    }
  }

  renderRows() {
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.repaint();
    }
  }
}
